import { DrawerHeader } from "../../../../components/drawer/drawer-header";
import {
  FormHeaderTextarea,
  FormFieldName,
  FormInput,
  FormSelect
} from "../../../../components/form/form";
import { memo, useState, useEffect, useMemo, useRef } from "react";
import { useTaskEdit } from "./task-edit.hook";
import { Drawer } from "../../../../components/drawer/drawer";
import { PageList, PageListAdd } from "./page-list";
import { TaskIconPicker } from "./icon-picker";
import { BlockAdd, BlockBadge } from "../../../../components/block/block";
import SelfAssignedSwitch from "./self-assigned-switch";
import {
  COUNTER,
  COUNTER_ERROR_MESSAGES,
  TASK_TYPE_LABELS,
  TASK_TYPE_OPTIONS,
} from "./counter-fields";
import { CATEGORY_ERROR_MESSAGES } from "./task-category";
import { Form, Tooltip, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useProjectNavigate } from "../../../../common/utils/useProjectNavigate";
import TagsSelect from "../../../../components/tags-select/tags-select";
import DiscardModal from "../../../../components/modals/discard/discard-modal.js";
import ApiTaskManagement from "../../../../api/task-managements";
import { Editor } from "../../../../components/editor/editor";
import TaskReminders from "./task-reminders";
import { refreshSignedMedia } from "../../../../components/editor/refreshSignedMedia";
import { descriptionEditorKey as buildDescriptionEditorKey } from "./description-editor-key";
import { resolveViewTask } from "./resolve-view-task";
import ChangesDiffModalComponent from "../../../../components/modals/change-logs/changes-diff-modal.js";
import AssociationsBlock from "../../../../components/associations/associations-block";

const TaskCardInside = memo(
  ({
    task,
    categories,
    plans,
    cohorts,
    close,
    editMode,
    createTask,
    updateTask,
    cancelEditing,
    deleteTask,
    toggleActive,
    toEditMode,
    pages = [],
    onlyView,
    setShowVersionPage,
    onDiscard,
    onPublish,
    onOpenTask,
    onDuplicate
  }) => {
    const navigate = useProjectNavigate();

    // ---------------------------
    // NEW: fetch full task by id (for associations + any missing fields)
    // ---------------------------
    const [fetchedTask, setFetchedTask] = useState(null);
    const [isLoadingTask, setIsLoadingTask] = useState(false);

    useEffect(() => {
      const id = task?.id;
      if (!id) {
        setFetchedTask(null);
        return;
      }

      let cancelled = false;
      setFetchedTask((prev) => (prev && String(prev.id) === String(id) ? prev : null));

      (async () => {
        setIsLoadingTask(true);
        try {
          const resp = await ApiTaskManagement.getById(id);
          const full = resp?.data?.task || resp?.data || null;
          if (cancelled) return;
          setFetchedTask(full);
        } catch (e) {
          console.error("❌ Failed to load task by id:", e);
        } finally {
          if (!cancelled) setIsLoadingTask(false);
        }
      })();

      return () => {
        cancelled = true;
      };
    }, [task?.id]);

    // choose data source (like ArticleModal)
    const viewTask = useMemo(() => resolveViewTask(task, fetchedTask), [task, fetchedTask]);

    // use viewTask in editor hook (so it sees the latest)
    const control = useTaskEdit({
      task: viewTask,
      createTask,
      updateTask,
      pages,
      cancelEditing,
      close,
    });

    // The description is rich text (LF360-1852). Its images and video carry signed
    // URLs that expire in five minutes, so the content is re-signed before the editor
    // is handed it, and the editor is remounted to pick it up — it only reads
    // `content` on mount.
    //
    // Remounting mid-typing would cost the admin their cursor, so what the editor
    // itself just produced is recognised and ignored here; only content that arrives
    // from elsewhere (the task loading, a different task, leaving edit mode) counts.
    const [descriptionContent, setDescriptionContent] = useState("");
    const [descriptionEditorKey, setDescriptionEditorKey] = useState("description-empty");
    const emittedDescriptionRef = useRef(null);
    const editedTaskIdRef = useRef(null);

    const onDescriptionChange = (value) => {
      emittedDescriptionRef.current = typeof value === "string" ? value : null;
      control.description.update(value);
    };

    useEffect(() => {
      const incoming = control.description.value || "";
      // Two tasks can hold the same text (or none), and the editor keeps its own
      // instance and undo history — so a different task always gets a new editor,
      // or an admin could undo their way into the previous task's content and save
      // it here.
      const openedAnotherTask = editedTaskIdRef.current !== (viewTask?.id || null);
      if (!openedAnotherTask && incoming === emittedDescriptionRef.current) { return; }
      editedTaskIdRef.current = viewTask?.id || null;

      let cancelled = false;

      // show what just arrived before the fresh URLs do, or the editor keeps the
      // previous task's description on screen while this one is being signed
      emittedDescriptionRef.current = incoming;
      setDescriptionContent(incoming);
      setDescriptionEditorKey(buildDescriptionEditorKey(viewTask?.id, editMode));

      (async () => {
        const refreshed = await refreshSignedMedia(incoming);
        if (cancelled || refreshed === incoming) { return; }
        setDescriptionContent(refreshed || "");
        setDescriptionEditorKey(buildDescriptionEditorKey(viewTask?.id, editMode, "signed"));
      })();

      return () => { cancelled = true; };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewTask?.id, editMode, control.description.value]);

    const [isChanged, setChanged] = useState(false);
    const [isDiscardModalOpen, setDiscardModalOpen] = useState(false);

    const [isOpenChangeLogsModal, setIsOpenChangeLogsModal] = useState(false);
    const [diffLoading, setDiffLoading] = useState(false);
    const [pendingVersion, setPendingVersion] = useState(null);
    const [activeVersion, setActiveVersion] = useState(null);
    const [diffFields, setDiffFields] = useState([]);

    const sortBy = (arr = [], pick = (x) => x?.sort ?? 0) =>
      (arr || []).slice().sort((a, b) => {
        const aa = pick(a) ?? 0;
        const bb = pick(b) ?? 0;
        if (aa === bb) return 0;
        return aa < bb ? -1 : 1;
      });

    const toLine = (label, value) => `${label}: ${value ?? ""}`;
    const boolStr = (v) => (v ? "true" : "false");

    const serializeQuestionsToText = (src) => {
      if (!src) return "";
      const qs = src?.questions || src?.Questions || [];
      if (!Array.isArray(qs) || qs.length === 0) return "";

      const safe = (s) => (s == null ? "" : String(s));

      const pickAnswers = (q) =>
        sortBy(q?.answers || q?.Answers || [], (a) => a?.sort)
          .map((a) => safe(a?.text))
          .filter((t) => t.length > 0);

      const blocks = sortBy(qs, (q) => q?.sort).map((q) => {
        const lines = [];
        lines.push(toLine("Text", safe(q?.text)));
        lines.push(toLine("Type", safe(q?.type)));
        lines.push(toLine("Required", boolStr(!!q?.required)));

        const desc = safe(q?.description).trim();
        if (desc) lines.push(toLine("Description", desc));

        const it = safe(q?.instructionsTitle).trim();
        if (it) lines.push(toLine("Instructions title", it));

        const ins = safe(q?.instructions).trim();
        if (ins) lines.push(toLine("Instructions", ins));

        const answers = pickAnswers(q);
        lines.push(toLine("Answers", answers.join(" | ")));

        return lines.join(", \n");
      });

      return blocks.join("\n\n");
    };

    useEffect(() => {
      if (viewTask?.status === "pending") {
        setChanged(true);
      }
    }, [viewTask?.status]);

    const handleOpenEditor = async () => {
      navigate(`/content/tasks/${viewTask.id}?view=version`);
    };

    const sortByTitle = (arr = []) =>
      (arr || []).slice().sort((a, b) => (a?.title || '').localeCompare(b?.title || ''));

    const composeTaskForDiff = (src) => {
      if (!src || !src?.id) return null;

      const categoryId =
        typeof src?.category === 'object' ? src?.category?.id : (src?.categoryId ?? null);

      const normEntityArray = (arr = []) =>
        (arr || []).map(x => (x && typeof x === 'object' ? x : { id: x }));

      return {
        id: src?.id,
        title: src?.title ?? '',
        description: src?.description ?? '',
        categoryId,
        taskType: src?.taskType ?? 'regular',
        // LF360-1874: in the snapshot the version diff is built from, so a
        // published change to what a task counts is visible the way a changed
        // title is.
        counterUnits: src?.counterUnits ?? '',
        counterGoal: src?.counterGoal ?? null,
        selfAssigned: !!(src?.selfAssigned ?? src?.isSelfAssigned),
        cohorts: normEntityArray(sortByTitle(src?.cohorts, 'name')),
        pages: normEntityArray(sortByTitle(src?.pages)),
        questionsText: serializeQuestionsToText(src),
      };
    };

    const buildTaskDiffFields = (categories, cohorts) => ([
      { key: 'title',        label: 'Title',          type: 'string' },
      { key: 'description',  label: 'Description',    type: 'string' },
      {
        key: 'categoryId',   label: 'Category',       type: 'entity',
        resolve: (id) => categories.find(c => c.id === id)?.name ?? '—',
        getId: (v) => (typeof v === 'object' ? v?.id : v),
      },
      {
        key: 'cohorts',        label: 'Cohorts',          type: 'array-entity',
        resolveMany: (vals = []) => {
          const ids = vals.map(x => (typeof x === 'object' ? x?.id : x));
          const names = (cohorts || [])
            .filter(c => ids.includes(c.id))
            .map(c => c.name || `#${c.id}`)
            .sort((a, b) => a.localeCompare(b));
          return names;
        },
        getId: (x) => (typeof x === 'object' ? x?.id : x),
      },
      {
        key: 'taskType',     label: 'Type',           type: 'enum',
        displayMap: TASK_TYPE_LABELS,
      },
      { key: 'counterUnits', label: 'Units',          type: 'string' },
      { key: 'counterGoal',  label: 'Goal',           type: 'string' },
      { key: 'selfAssigned', label: 'Self-assigned',  type: 'boolean' },
      {
        key: 'pages',        label: 'Pages',          type: 'array-entity',
        resolveMany: (arr = []) =>
          (arr || [])
            .map(p => (typeof p === 'object' ? (p.title || `#${p.id}`) : String(p)))
            .sort((a, b) => a.localeCompare(b)),
        getId: (x) => (typeof x === 'object' ? x?.id : x),
      },
      { key: 'questionsText', label: 'Questions',     type: 'text' },
    ]);

    const openChangeLogs = async () => {
      try {
        setDiffLoading(true);

        const [pendingResp, activeResp] = await Promise.all([
          ApiTaskManagement.getById(viewTask.id),
          ApiTaskManagement.getById(viewTask.id, { status: 'active' }),
        ]);
        const pendingRaw = pendingResp?.data?.task || pendingResp?.data || null;
        const activeRaw = activeResp?.data?.task || activeResp?.data || null;

        setPendingVersion(composeTaskForDiff(pendingRaw));
        setActiveVersion(composeTaskForDiff(activeRaw));
        setDiffFields(buildTaskDiffFields(categories, cohorts));

        setIsOpenChangeLogsModal(true);
      } finally {
        setDiffLoading(false);
      }
    };

    const handleChangeLogsCancel = () => setIsOpenChangeLogsModal(false);

    // ---------------------------
    // NEW: associations groups parser (same logic as article modal)
    // ---------------------------
    const associationGroups = useMemo(() => {
      const src = viewTask?.associations;
      if (!src) return [];

      if (Array.isArray(src)) {
        return src
          .filter(Boolean)
          .map((g) => ({
            key: g.key || g.title,
            title: g.title,
            count: Number.isInteger(g.count) ? g.count : undefined,
            items: Array.isArray(g.items)
              ? g.items.filter(Boolean).map((it) => ({
                  id: it.id,
                  label: it.label,
                  href: it.href,
                  icon: it.icon,
                }))
              : [],
          }))
          .filter((g) => g.title);
      }

      if (typeof src === "object") {
        return Object.entries(src)
          .map(([key, g]) => ({
            key,
            title: g?.title || key,
            count: Number.isInteger(g?.count) ? g.count : undefined,
            items: Array.isArray(g?.items)
              ? g.items.map((it) => ({
                  id: it.id,
                  label: it.label,
                  href: it.href,
                  icon: it.icon,
                }))
              : [],
          }))
          .filter((g) => g.title);
      }

      return [];
    }, [viewTask]);

    // ---------------------------
    // render
    // ---------------------------
    if (!viewTask?.id) return null;

    return (
      <div style={{ position: "relative", minHeight: "100%" }}>
        {isLoadingTask ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 20,
              background: "rgba(255, 255, 255, 0.72)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Spin
              indicator={
                <LoadingOutlined
                  style={{ fontSize: 48 }}
                  spin
                />
              }
            />
          </div>
        ) : null}

        {/* `data-ready`: the card has read the task and saves what is changed
            from here on (`ready` of task-edit.hook.js); an edit made before it
            is overwritten by that read. For the browser tests (LF360-1981). */}
        <div
          style={{
            opacity: isLoadingTask ? 0.35 : 1,
            pointerEvents: isLoadingTask ? "none" : "auto",
          }}
          data-testid="task-card"
          data-ready={control.ready && !isLoadingTask ? "true" : "false"}
        >
          <DrawerHeader
            title={
              <TaskIconPicker
                task={control.icon.value}
                onIconSelect={editMode ? control.icon.update : undefined}
              />
            }
            isEditing={editMode}
            isChanged={isChanged}
            onEdit={onlyView ? undefined : toEditMode}
            onClose={close}
            onPublish={async () => {
              await onPublish(viewTask.id);
              close();
            }}
            onDiscard={() => setDiscardModalOpen(true)}
            onCancel={control.cancel}
            onViewChanges={viewTask?.id ? openChangeLogs : undefined}
            onToggleActive={() => toggleActive?.(viewTask)}
            isActive={!!viewTask?.isActive}
            publishButton={
              !!viewTask?.isActive &&
              (viewTask.statusSummary === "inactive" || !viewTask.statusSummary)
            }
            onDelete={() =>
              deleteTask?.(viewTask?.id, viewTask?.title)
            }
            resourceTitle={viewTask?.title}
            onlyView={onlyView}
            onOpenTask={onOpenTask}
            onDuplicate={() => onDuplicate?.(viewTask)}
          />

          <div style={{ paddingBottom: 24 }} >
            <FormHeaderTextarea
                readonly={!editMode}
              placeholder={"Title"}
              defaultValue={control.title.value}
              onChange={control.title.update}
              maxLength={100}
            />
          </div>


          {/* LF360-2011: required, because the category is where the task's
              LifePoints come from. Shown while editing so the admin finds out
              before Publish, which the server refuses without one. */}
          <Form.Item
            name="categoryId"
            initialValue={control.category.value}
            validateStatus={editMode && !control.category.value ? "error" : undefined}
            help={editMode && !control.category.value
              ? CATEGORY_ERROR_MESSAGES.CATEGORY_REQUIRED
              : undefined}>
            <FormFieldName text="Category:" />
            <FormSelect
              options={categories.map((c) => ({
                value: c.id,
                label: c.name,
                key: c.id,
              }))}
              defaultValue={categories
                .map((cat) => ({ value: cat.id, key: cat.id, label: cat.name }))
                .find((c) => c.value === control.category.value)}
              onSelect={(value) => control.category.change?.(value)}
              placeholder={editMode ? "Select category..." : undefined}
              style={{ minWidth: 160 }}
              readonly={!editMode}
              getPopupContainer={trigger => trigger.parentNode}
            />
          </Form.Item>

          <Form.Item name={"cohorts"} initialValue={control.cohorts.value.map((c) => c.id )}>
            <FormFieldName text="Cohorts:" />
            <div style={{ marginLeft: 11 }}>
              <TagsSelect
                options={(cohorts || []).map((c) => ({
                  value: c.id,
                  label: c.name,
                  key: c.id,
                }))}
                defaultValue={control.cohorts.value
                  .slice()
                  .sort((a, b) => (a.value || '').localeCompare(b.value || ''))
                  .map((c) => ({
                    value: c.id,
                    label: c.value,
                    key: c.id,
                  }))}
                onChange={control.cohorts.onChange}
                onCreate={control.cohorts.onCreate}
                readonly={!editMode}
                placeholder="+ Add Cohort"
                noMarginLeft={true}
                isEditing={!editMode}
              />
            </div>
          </Form.Item>

          <SelfAssignedSwitch control={control} editMode={editMode} />

          <Form.Item name="taskType" initialValue={control.taskType.value}>
            <FormFieldName text="Type:" />
            <FormSelect
              options={TASK_TYPE_OPTIONS}
              defaultValue={{
                value: control.taskType.value,
                label: TASK_TYPE_LABELS[control.taskType.value] ?? TASK_TYPE_LABELS.regular,
              }}
              onSelect={(value) => control.taskType && control.taskType.change?.(value)}
              placeholder={editMode ? 'Select task type...' : undefined}
              style={{ minWidth: 160 }}
              readonly={!editMode || control?.publishedOnce}
              getPopupContainer={trigger => trigger.parentNode}
            />
          </Form.Item>

          {/* LF360-1874 — what a counter counts, and where it suggests
              stopping. Only for this type, because they mean nothing for the
              other two and the server refuses them there.

              The Goal box is deliberately allowed to be empty: that is a
              counter with no goal, which draws no progress bar for the subject
              rather than one that can never fill. The error line mirrors the
              server's own refusals — it exists so an admin finds out while
              typing, not so the client decides. */}
          {control.taskType?.value === COUNTER && (
            <>
              <Form.Item
                name="counterUnits"
                validateStatus={control.counter.error === 'COUNTER_UNITS_REQUIRED' && editMode ? 'error' : undefined}
                help={control.counter.error === 'COUNTER_UNITS_REQUIRED' && editMode
                  ? COUNTER_ERROR_MESSAGES.COUNTER_UNITS_REQUIRED
                  : undefined}>
                <FormFieldName text="Units:" />
                <FormInput
                  value={control.counter.units}
                  onChange={(event) => control.counter.changeUnits(event.target.value)}
                  placeholder={editMode ? 'glasses, km, minutes…' : undefined}
                  readonly={!editMode}
                  maxLength={50}
                  data-testid="task-counter-units"
                />
              </Form.Item>

              <Form.Item
                name="counterGoal"
                validateStatus={control.counter.error === 'COUNTER_GOAL_INVALID' && editMode ? 'error' : undefined}
                help={control.counter.error === 'COUNTER_GOAL_INVALID' && editMode
                  ? COUNTER_ERROR_MESSAGES.COUNTER_GOAL_INVALID
                  : undefined}>
                <FormFieldName text="Goal:" />
                <FormInput
                  type="number"
                  value={control.counter.goal}
                  onChange={(event) => control.counter.changeGoal(event.target.value)}
                  placeholder={editMode ? 'Optional' : undefined}
                  readonly={!editMode}
                  data-testid="task-counter-goal"
                />
              </Form.Item>

              {control.counter.error && editMode ? (
                <div className="lf-form-error" role="alert" data-testid="task-counter-error">
                  {COUNTER_ERROR_MESSAGES[control.counter.error]}
                </div>
              ) : null}
            </>
          )}

          {/* LF360-1815: the questions are authored in the scenario editor —
              survey questions for a survey task, the completion survey for the
              other two.

              LF360-1874: three types now, not two, and a counter has this for
              the same reason a regular task does. A counter MAY carry the
              follow-up questionnaire; the count itself is task data rather than
              one of its questions, which is why it is configured above and
              lands in the report ahead of the answers. */}
          <div className="article-modal-tags-label">
            {control.taskType && control.taskType.value === 'survey'
              ? 'Survey questions'
              : 'Task completion survey'}
          </div>
          {viewTask?.id && control?.DailyTaskVersion?.id ? (
            control?.DailyTaskVersion?.status === 'pending' ? (
              <BlockBadge
                title="Open in editor"
                hideIcon={true}
                onClick={handleOpenEditor}
                status="pending"
              />
            ) : (
              <BlockAdd
                title="Open in editor"
                hideIcon={true}
                onClick={handleOpenEditor}
              />
            )
          ) : (
            <Tooltip title="The task draft must be saved before opening the editor." arrow={true}>
              <div style={{ cursor: 'not-allowed' }}>
                <BlockAdd title="Open in editor" hideIcon={true} />
              </div>
            </Tooltip>
          )}

          {/* LF360-1852: the description sits below the completion survey and above
              the articles, and is written with the editor the articles use — so an
              admin can put images and video into it the same way. */}
          <Form.Item name="description">
            <div className="task-description-field" data-testid="task-description">
              <FormFieldName text="Description" />
              <Editor
                key={descriptionEditorKey}
                content={descriptionContent}
                readonly={!editMode}
                onChange={onDescriptionChange}
                dailyTaskId={viewTask?.id}
              />
            </div>
          </Form.Item>

          {/* LF360-1940: the reminders every plan gets from this task, below the
              description as the ticket asks. Read-only outside edit mode like
              everything else on the card. */}
          <TaskReminders
            value={control.reminders.value}
            onChange={control.reminders.change}
            readOnly={!editMode}
          />

          <PageList
            pages={control.pages.value}
            editMode={editMode}
            onRemove={control.pages.remove}
            onOpenPage={(page) => navigate(`/content/articles/${page.id}`)}
          />

          <PageListAdd
            attachedIds={control.pages.value.map((page) => page.id)}
            onAdd={control.pages.add}
            editMode={editMode}
            taskTitle={control.title?.value}
          />

          {associationGroups?.length ? (
            <AssociationsBlock groups={associationGroups} />
          ) : null}

          <DiscardModal
            open={isDiscardModalOpen}
            onConfirm={async () => {
              await onDiscard(viewTask.id);
              setDiscardModalOpen(false);
              close();
            }}
            onCancel={() => setDiscardModalOpen(false)}
          />

          <ChangesDiffModalComponent
            key={`diff-${viewTask?.id || 'new'}`}
            isModalOpen={isOpenChangeLogsModal}
            handleCancel={handleChangeLogsCancel}
            title={"Changes"}
            loading={diffLoading}
            pending={pendingVersion}
            active={activeVersion}
            fields={diffFields}
            titleFieldKey="title"
          />
        </div>
      </div>
    );
  }
);

const TaskCard = ({
  task,
  categories,
  plans,
  cohorts,
  isOpen,
  close,
  editMode,
  createTask,
  updateTask,
  deleteTask,
  toEditMode,
  cancelEditing,
  pages,
  onlyView,
  setShowVersionPage,
  onDiscard,
  onPublish,
  toggleActive,
  onOpenTask,
  onDuplicate,
}) => {
  return (
    <Drawer open={isOpen} size={800} closable={false} onClose={close}>
      <div style={{ paddingBottom: 48 }}>
        {isOpen && (
          <Form>
            <TaskCardInside
              task={task}
              categories={categories}
              plans={plans}
              cohorts={cohorts}
              createTask={createTask}
              close={close}
              cancelEditing={cancelEditing}
              updateTask={updateTask}
              deleteTask={deleteTask}
              toEditMode={toEditMode}
              editMode={editMode}
              pages={pages}
              onlyView={onlyView}
              onOpenTask={onOpenTask}
              setShowVersionPage={setShowVersionPage}
              onDiscard={onDiscard}
              onPublish={onPublish}
              toggleActive={toggleActive}
              onDuplicate={onDuplicate}
            />
          </Form>
        )}
      </div>
    </Drawer>
  );
};

export default TaskCard;
